import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, ExternalLink, Banknote } from "lucide-react";
import { toast } from "sonner";

/**
 * Stripe Connect Express onboarding card for affiliates AND publishers.
 * Accepts a `kind` prop: "affiliate" (default) or "publisher".
 *
 * Endpoints used:
 *   GET  /api/{kind}/connect/status
 *   POST /api/{kind}/connect/account
 *   POST /api/{kind}/connect/onboarding-link
 */

const IconChip = ({ Icon }) => (
  <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0F172A] text-white shrink-0">
    <Icon className="size-4" strokeWidth={1.6} />
  </span>
);

export default function StripeConnectCard({ kind = "affiliate" }) {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const base = `/${kind}/connect`;

  const load = async () => {
    try {
      const { data } = await api.get(`${base}/status`);
      setState({ loading: false, ...data });
    } catch (e) {
      setState({ loading: false, enabled: false, error: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const createAccount = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/account`);
      toast.success("Stripe Connect account created — continue onboarding to enable payouts.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create Stripe Connect account");
    } finally { setBusy(false); }
  };

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/onboarding-link`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate onboarding link");
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200 bg-white" data-testid={`connect-card-loading-${kind}`}>
        <CardContent className="p-6 flex items-center gap-3 text-sm text-slate-500">
          <Loader2 className="size-4 animate-spin" /> Checking Stripe Connect status…
        </CardContent>
      </Card>
    );
  }

  // Disabled — server has no real Stripe key configured
  if (state.enabled === false) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200 bg-white overflow-hidden" data-testid={`connect-card-disabled-${kind}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <IconChip Icon={Banknote} />
            <div className="flex-1 leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 text-[15px]">Stripe Express payouts</span>
                <span className="text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">coming soon</span>
              </div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed max-w-lg">
                Our finance team is finalising the Stripe Connect integration for instant payouts.
                For now, request a payout below and we'll process it manually via Bank Transfer within 1–2 working days.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Has not created a Connect account yet
  if (!state.has_account) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200 bg-white overflow-hidden" data-testid={`connect-card-setup-${kind}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <IconChip Icon={Banknote} />
            <div className="flex-1 min-w-[240px] leading-tight">
              <div className="font-medium text-slate-900 text-[15px]">Set up Stripe Express payouts</div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">Get paid {kind === "publisher" ? "earnings" : "commissions"} directly to your bank account within hours of approval — powered by Stripe Connect.</div>
            </div>
            <Button onClick={createAccount} disabled={busy} className="rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20" data-testid={`connect-create-btn-${kind}`}>
              {busy ? <><Loader2 className="size-4 animate-spin mr-1.5" /> Setting up…</> : "Set up payouts"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Account exists — ready
  if (state.payouts_enabled) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200 bg-white overflow-hidden" data-testid={`connect-card-ready-${kind}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <IconChip Icon={Banknote} />
            <div className="flex-1 leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 text-[15px]">Express payouts enabled</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-[#22D3A6]/12 text-emerald-700 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="size-3" strokeWidth={2.4} /> live
                </span>
              </div>
              <div className="text-[11.5px] font-mono text-slate-500 mt-2">stripe acct · {(state.stripe_account_id || "").slice(-12)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Onboarding incomplete
  const dueCount = (state.currently_due || []).length;
  return (
    <Card className="rounded-xl shadow-soft border-slate-200 bg-white overflow-hidden" data-testid={`connect-card-onboarding-${kind}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <IconChip Icon={AlertCircle} />
          <div className="flex-1 min-w-[240px] leading-tight">
            <div className="font-medium text-slate-900 text-[15px]">Finish Stripe verification</div>
            <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
              {dueCount > 0
                ? `Stripe needs ${dueCount} more piece${dueCount === 1 ? "" : "s"} of info before payouts can be sent.`
                : "Your Stripe Connect account is being reviewed — finish verification to unlock instant payouts."}
            </div>
          </div>
          <Button onClick={startOnboarding} disabled={busy} className="rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20" data-testid={`connect-onboarding-btn-${kind}`}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><ExternalLink className="size-4 mr-1.5" /> Continue verification</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
